/**
 * Pure helpers for line-based truncation. Kept free of React/DOM so the
 * truncation math can be unit-tested directly, without mocking ResizeObserver,
 * scrollHeight, or getComputedStyle.
 */

/** Parse a CSS line-height into pixels, falling back to fontSize * 1.2 for 'normal'. */
export const parseLineHeight = (
  lineHeight: string,
  fontSize: string,
): number => {
  const parsed =
    lineHeight === 'normal'
      ? parseFloat(fontSize) * 1.2
      : parseFloat(lineHeight);
  return isNaN(parsed) ? 0 : parsed;
};

/** Number of rendered lines for a content height. Returns 0 when lineHeight is unusable. */
export const computeTotalLines = (
  scrollHeight: number,
  lineHeight: number,
): number => (lineHeight > 0 ? Math.ceil(scrollHeight / lineHeight) : 0);

interface LineState {
  /** True when there are more lines than the initial limit (controls button visibility). */
  hasHiddenLines: boolean;
  /** True when more lines remain to reveal (Show more vs Show less). */
  shouldShowMore: boolean;
}

/** Derive button/clamp state from the current line counts. */
export const deriveLineState = ({
  limitLines,
  visibleLines,
  totalLines,
}: {
  limitLines: number | undefined;
  visibleLines: number;
  totalLines: number;
}): LineState => ({
  hasHiddenLines: limitLines !== undefined && totalLines > limitLines,
  shouldShowMore: limitLines !== undefined && visibleLines < totalLines,
});

/** Next visible line count after a Show more click, capped at totalLines. */
export const nextVisibleLines = (
  visibleLines: number,
  stepLines: number,
  totalLines: number,
): number => Math.min(visibleLines + stepLines, totalLines);
